import React, { useCallback, useMemo, useState } from 'react';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import customerIcon from '../../assets/icons/pin-location-icon.svg';
import driverIcon from '../../assets/icons/car-icon.svg'
export interface ILocation {
  latitude: number;
  longitude: number;
}

interface VehicleTrackingMapProps {
  driverLocation: ILocation | null;
  customerLocation: ILocation | null;
}

export const VehicleTrackingMap: React.FC<VehicleTrackingMapProps> = ({
  driverLocation,
  customerLocation,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY!,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);

  // Always center over the customer location if available.
  const center = useMemo(() => {
    if (customerLocation) {
      return {
        lat: customerLocation.latitude,
        lng: customerLocation.longitude,
      };
    } else if (driverLocation) {
      return {
        lat: driverLocation.latitude,
        lng: driverLocation.longitude,
      };
    }
    return { lat: 0, lng: 0 }; // fallback center
  }, [customerLocation, driverLocation]);

  const onLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      setMap(mapInstance);
      // Always center on the customer location if available
      if (customerLocation) {
        mapInstance.setCenter({
          lat: customerLocation.latitude,
          lng: customerLocation.longitude,
        });
      } else if (driverLocation) {
        mapInstance.setCenter({
          lat: driverLocation.latitude,
          lng: driverLocation.longitude,
        });
      } else {
        mapInstance.setCenter(center);
      }
    },
    [customerLocation, driverLocation, center]
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (loadError) {
    return <div>Error loading maps</div>;
  }
  if (!isLoaded) {
    return <div>Loading Maps...</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={center}
      zoom={10}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {driverLocation && (
        <Marker
          position={{ lat: driverLocation.latitude, lng: driverLocation.longitude }}
          label="Driver"
          icon={{
            url: driverIcon,
            scaledSize: new window.google.maps.Size(40, 40),
          }}
        />
      )}
      {customerLocation && (
        <Marker
          position={{ lat: customerLocation.latitude, lng: customerLocation.longitude }}
          icon={{
            url: customerIcon,
            scaledSize: new window.google.maps.Size(40, 40),
          }}
        />
      )}
    </GoogleMap>
  );
};
